import { useTerms } from '@/features/shared/ops/kit';
import { useSearchParams } from 'react-router-dom';
import { useTeacherScope } from '@/lib/useTeacherScope';
import { useBase } from '@/lib/useBase';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  postCbtExams, postCbtExamsQuestions, postCbtExamsPush,
  getQuestionBanks, getQuestionBanksQuestions,
} from '@/api/teacher';
import {
  getClasses, getSubjects, getAssessmentComponents,
} from '@/api/owner';
import { AlertCircle, Check, ChevronLeft, ChevronRight, Send } from 'lucide-react';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};

const unwrap = (r: any) => r?.data?.data ?? r?.data;
// Response from /school/assessment-components is { components: [...], total_max: N }
// — not a flat array. Extract .components explicitly (same pattern as GradingPage).
const extractComponents = (raw: any): any[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.components)) return raw.components;
  if (Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw.data?.components)) return raw.data.components;
  if (Array.isArray(raw.data?.data)) return raw.data.data;
  return [];
};


type Kind = 'ca' | 'test' | 'midterm' | 'project' | 'exam' | 'quiz' | 'practice' | 'other';
type ShowResult = 'never' | 'after_submit' | 'after_release';

export function OwnerCbtCreatePage() {
  const base = useBase();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState(1);
  const [error, setError] = useState<string | null>(null);

  // form state
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<Kind>('test');
  const [subjectId, setSubjectId] = useState<number | ''>('');
  const [termId, setTermId] = useState<number | ''>('');
  const [componentId, setComponentId] = useState<number | ''>('');
  const [instructions, setInstructions] = useState('');
  const [duration, setDuration] = useState('45');

  const [classIds, setClassIds] = useState<number[]>([]);

  const [opensAt, setOpensAt] = useState('');
  const [closesAt, setClosesAt] = useState('');
  const [openNow, setOpenNow] = useState(true);
  const [allowResume, setAllowResume] = useState(true);
  const [maxAttempts, setMaxAttempts] = useState('1');

  const [shuffleQuestions, setShuffleQuestions] = useState(false);
  const [shuffleOptions, setShuffleOptions] = useState(false);
  const [showResult, setShowResult] = useState<ShowResult>('after_submit');
  const [passMark, setPassMark] = useState('');

  // question picker
  const [questionMode, setQuestionMode] = useState<'specific' | 'random'>('specific');
  const [specificIds, setSpecificIds] = useState<number[]>([]);
  const [randomBankId, setRandomBankId] = useState<number | ''>('');
  const [randomCount, setRandomCount] = useState('10');
  const [randomDifficulty, setRandomDifficulty] = useState('');
  const [randomType, setRandomType] = useState('');
  const [selectedBankForList, setSelectedBankForList] = useState<number | ''>('');

  const [pushAfter, setPushAfter] = useState(false);

  // queries
  const classesQ = useQuery({ queryKey: ['owner-classes'], queryFn: () => getClasses({ per_page: 200 }).then(unwrap) });
  const subjectsQ = useQuery({ queryKey: ['owner-subjects'], queryFn: () => getSubjects({ per_page: 500 }).then(unwrap) });
  const { terms: allTerms } = useTerms();
  // Filter components by the class level of the first picked class.
  // Uses classesQ.data directly (query reference) so it doesn't depend on
  // the derived `classes` array being declared before this point.
  const firstClassLevelId = useMemo(() => {
    if (classIds.length === 0) return '';
    const rows: any[] = Array.isArray(classesQ.data)
      ? classesQ.data
      : (classesQ.data?.data ?? []);
    const firstClass = rows.find((c: any) => c.id === classIds[0]);
    return firstClass?.class_level_id ?? '';
  }, [classIds, classesQ.data]);

  const componentsQ = useQuery({
    // the columns of the first chosen class (its own set, or the all-classes set)
    queryKey: ['owner-assessment-components', classIds[0] ?? null, firstClassLevelId],
    queryFn: () => getAssessmentComponents(
      classIds[0] ? { class_id: classIds[0] } : undefined
    ).then(unwrap),
  });

  // Always fetch ALL components too — cheap (~5-20 rows), avoids the empty-dropdown bug.
  // This is the same list Score Entry uses as fallback.
  const allComponentsQ = useQuery({
    queryKey: ['owner-assessment-components-all'],
    queryFn: () => getAssessmentComponents().then(unwrap),
  });
  const banksQ = useQuery({ queryKey: ['question-banks'], queryFn: () => getQuestionBanks().then(unwrap) });
  const bankQuestionsQ = useQuery({
    queryKey: ['bank-questions-for-pick', selectedBankForList],
    queryFn: () => selectedBankForList ? getQuestionBanksQuestions(selectedBankForList).then(unwrap) : Promise.resolve([]),
    enabled: !!selectedBankForList,
  });

  const scope = useTeacherScope();
  const classes = scope.classesFor(arr<any>(classesQ.data), subjectId || null);
  const subjects = scope.subjects(arr<any>(subjectsQ.data), classIds[0]);
  const terms = allTerms;
  const [params] = useSearchParams();
  useEffect(() => {
    const sid = Number(params.get('subject_id')); const cid = Number(params.get('class_id'));
    if (sid && !subjectId) setSubjectId(sid);
    if (cid && !classIds.length) setClassIds([cid]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!termId) { const cur = terms.find((t: any) => t.is_current); if (cur) setTermId(cur.id); } }, [terms.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const filteredComponents = extractComponents(componentsQ.data);
  const allComponents = extractComponents(allComponentsQ.data);
  // Use class-level set if it has items; otherwise fall back to ALL components
  const components = filteredComponents.length > 0 ? filteredComponents : allComponents;
  const usingFallback = filteredComponents.length === 0 && allComponents.length > 0;
  const banks = arr<any>(banksQ.data);
  const bankQuestions = arr<any>(bankQuestionsQ.data);

  const createM = useMutation({
    mutationFn: async () => {
      const body: any = {
        title: title.trim(),
        kind,
        duration_minutes: Number(duration),
        class_ids: classIds,
      };
      if (subjectId) body.subject_id = Number(subjectId);
      if (termId) body.term_id = Number(termId);
      if (componentId) body.assessment_component_id = Number(componentId);
      if (instructions.trim()) body.instructions = instructions.trim();
      if (!openNow) {
        if (opensAt) body.opens_at = opensAt;
        if (closesAt) body.closes_at = closesAt;
      }
      body.allow_resume = allowResume;
      body.max_attempts = Number(maxAttempts) || 1;
      body.shuffle_questions = shuffleQuestions;
      body.shuffle_options = shuffleOptions;
      body.show_result = showResult;
      if (passMark) body.pass_mark = Number(passMark);

      const exam: any = await postCbtExams(body);
      const ex = unwrap(exam);

      // Add questions
      if (questionMode === 'specific' && specificIds.length > 0) {
        await postCbtExamsQuestions(ex.id, {
          questions: specificIds.map(qid => ({ question_id: qid })),
        });
      } else if (questionMode === 'random' && randomBankId) {
        const payload: any = {
          random: {
            bank_id: Number(randomBankId),
            count: Number(randomCount),
          },
        };
        if (randomDifficulty) payload.random.difficulty = randomDifficulty;
        if (randomType) payload.random.type = randomType;
        await postCbtExamsQuestions(ex.id, payload);
      }

      if (pushAfter) {
        await postCbtExamsPush(ex.id, { target: 'class' });
      }

      return ex;
    },
    onSuccess: (exam: any) => {
      qc.invalidateQueries({ queryKey: ['owner-school-cbt-exams'] });
      alert(`✅ Exam created${pushAfter ? ' and pushed to classes' : ''}.`);
      nav(base + '/cbt-exams');
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.message
        || (e?.response?.data?.errors && Object.entries(e.response.data.errors).map(([k, v]: any) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('\n'))
        || 'Create failed';
      setError(String(msg));
    },
  });

  // ── validation per step ──
  const stepValid = (s: number): boolean => {
    if (s === 1) return title.trim().length > 0 && Number(duration) >= 5;
    if (s === 2) return classIds.length > 0;
    return true;
  };

  const submit = () => {
    setError(null);
    if (!stepValid(1)) { setError('Fill basics first.'); return; }
    if (!stepValid(2)) { setError('Select at least one class.'); return; }
    createM.mutate();
  };

  const steps = ['Basics', 'Classes', 'Schedule', 'Rules', 'Questions'];

  return (
    <div className="max-w-[1200px] mx-auto p-6 space-y-6">
      <div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Teaching tools · CBT Exams</p>
        <h1 className="text-3xl font-extrabold text-slate-900">New CBT exam</h1>
        <p className="text-slate-500 text-sm mt-1">Step {step} of 5 · {steps[step - 1]}</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 whitespace-pre-wrap flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {/* step dots */}
      <div className="flex items-center gap-2">
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            <button onClick={() => setStep(i + 1)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition ${
                step === i + 1 ? 'bg-[#0f172a] text-white' :
                step > i + 1 ? 'bg-green-100 text-green-800' :
                'bg-slate-100 text-slate-500'
              }`}>
              {step > i + 1 ? <Check className="w-3 h-3" /> : <span>{i + 1}</span>}
              {s}
            </button>
            {i < steps.length - 1 && <div className="h-px flex-1 bg-slate-200" />}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Title *</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Basic Science Test 2"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Exam type *
                  <span className="text-xs font-normal text-slate-500 block mt-0.5">
                    Just a category label — the result column is chosen on Step 2
                  </span>
                </label>
                <select value={kind} onChange={(e) => setKind(e.target.value as Kind)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="ca">CA (Continuous Assessment)</option>
                  <option value="test">Test</option>
                  <option value="midterm">Midterm Test</option>
                  <option value="project">Project</option>
                  <option value="exam">Exam</option>
                  <option value="quiz">Quiz</option>
                  <option value="practice">Practice (no result push)</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Duration (min) *</label>
                <input type="number" min={5} max={300} value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Subject</label>
                <select value={subjectId} onChange={(e) => setSubjectId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="">—</option>
                  {subjects.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Term</label>
              <select value={termId} onChange={(e) => setTermId(e.target.value ? Number(e.target.value) : '')}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">—</option>
                {terms.map((t: any) => <option key={t.id} value={t.id}>{t.label}{t.is_current ? ' (current)' : ''}</option>)}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Result column is chosen on Step 2 (after class) — some classes have different components.
              </p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Instructions</label>
              <textarea rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)}
                placeholder="Rules for students before they start"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-slate-700">Which classes take this exam? *</label>
            {classes.length === 0 ? (
              <p className="text-sm text-slate-500 italic">No classes exist — add one first.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {classes.map((c: any) => {
                  const checked = classIds.includes(c.id);
                  return (
                    <label key={c.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer ${
                      checked ? 'border-[#0f172a] bg-slate-50' : 'border-slate-200 hover:bg-slate-50'
                    }`}>
                      <input type="checkbox" checked={checked}
                        onChange={() => setClassIds(checked ? classIds.filter(x => x !== c.id) : [...classIds, c.id])} />
                      <span className="text-sm font-semibold text-slate-900">
                        {c.name}{c.arm ? ` · ${c.arm}` : ''}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            <p className="text-xs text-slate-500">{classIds.length} class(es) selected</p>

            {classIds.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
                <label className="block text-sm font-semibold text-slate-700">
                  Result column
                </label>
                <p className="text-xs text-slate-500 -mt-2">
                  {usingFallback
                    ? "No columns specific to this class level — showing all school-wide columns. Choose one to push scores into the report card on release, or leave as Don't push for a standalone/practice exam."
                    : "These are the result columns for the selected class's level. Choose one to push scores into the report card on release, or leave as Don't push for a standalone/practice exam."}
                </p>
                {(componentsQ.isLoading || allComponentsQ.isLoading) ? (
                  <p className="text-xs text-slate-400">Loading components…</p>
                ) : components.length === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800">
                    No result columns exist at all. Create them in <b>Grading &amp; Weights</b> first,
                    or leave this as &ldquo;Don&rsquo;t push to results&rdquo; and continue.
                  </div>
                ) : (
                  <select
                    value={componentId}
                    onChange={(e) => setComponentId(e.target.value ? Number(e.target.value) : '')}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                    <option value="">Don't push to results</option>
                    {components.map((c: any) => (
                      <option key={c.id} value={c.id}>{c.code} · {c.name} (max {c.max_score})</option>
                    ))}
                  </select>
                )}
                {componentId && (
                  <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">
                    ✓ On release, computed scores will be scaled to max {components.find((c: any) => c.id === Number(componentId))?.max_score} and pushed to this column.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
              <input type="checkbox" checked={openNow} onChange={(e) => setOpenNow(e.target.checked)} />
              Open immediately when pushed
            </label>
            {!openNow && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Opens at</label>
                  <input type="datetime-local" value={opensAt} onChange={(e) => setOpensAt(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Closes at</label>
                  <input type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Max attempts</label>
                <input type="number" min={1} max={5} value={maxAttempts}
                  onChange={(e) => setMaxAttempts(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer mt-7">
                  <input type="checkbox" checked={allowResume} onChange={(e) => setAllowResume(e.target.checked)} />
                  Allow resume if student leaves
                </label>
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
                <input type="checkbox" checked={shuffleQuestions} onChange={(e) => setShuffleQuestions(e.target.checked)} />
                Shuffle questions
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
                <input type="checkbox" checked={shuffleOptions} onChange={(e) => setShuffleOptions(e.target.checked)} />
                Shuffle options
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Show result to student</label>
                <select value={showResult} onChange={(e) => setShowResult(e.target.value as ShowResult)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="never">Never</option>
                  <option value="after_submit">After submit</option>
                  <option value="after_release">After teacher releases</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Pass mark (%)</label>
                <input type="number" min={0} max={100} value={passMark} onChange={(e) => setPassMark(e.target.value)}
                  placeholder="e.g. 50"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                <p className="text-xs text-slate-500 mt-1">A percentage of the total marks: 50 means half the marks.</p>
              </div>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4">
            <div className="inline-flex bg-slate-100 rounded-lg p-1">
              <button type="button" onClick={() => setQuestionMode('specific')}
                className={`px-3 py-1.5 text-xs font-bold rounded ${questionMode === 'specific' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}>
                Pick specific
              </button>
              <button type="button" onClick={() => setQuestionMode('random')}
                className={`px-3 py-1.5 text-xs font-bold rounded ${questionMode === 'random' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}>
                Random from bank
              </button>
            </div>

            {questionMode === 'specific' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Pick from bank</label>
                  <select value={selectedBankForList}
                    onChange={(e) => { setSelectedBankForList(e.target.value ? Number(e.target.value) : ''); setSpecificIds([]); }}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                    <option value="">Choose a bank…</option>
                    {banks.map((b: any) => (
                      <option key={b.id} value={b.id}>{b.name} ({b.questions ?? 0})</option>
                    ))}
                  </select>
                </div>
                {bankQuestionsQ.isLoading && <p className="text-sm text-slate-500">Loading questions…</p>}
                {bankQuestions.length > 0 && (
                  <div className="border border-slate-200 rounded-lg max-h-[300px] overflow-y-auto divide-y divide-slate-100">
                    {bankQuestions.map((q: any) => {
                      const checked = specificIds.includes(q.id);
                      return (
                        <label key={q.id} className="flex items-start gap-2 p-2 hover:bg-slate-50 cursor-pointer">
                          <input type="checkbox" checked={checked} className="mt-0.5"
                            onChange={() => setSpecificIds(checked ? specificIds.filter(x => x !== q.id) : [...specificIds, q.id])} />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-slate-900 line-clamp-2">{q.body}</p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {q.type} · {q.marks ?? 1} mark(s)
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-slate-500">{specificIds.length} question(s) selected</p>
              </div>
            )}

            {questionMode === 'random' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Bank *</label>
                  <select value={randomBankId} onChange={(e) => setRandomBankId(e.target.value ? Number(e.target.value) : '')}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                    <option value="">Choose a bank…</option>
                    {banks.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">How many *</label>
                  <input type="number" min={1} max={100} value={randomCount}
                    onChange={(e) => setRandomCount(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Difficulty</label>
                  <select value={randomDifficulty} onChange={(e) => setRandomDifficulty(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                    <option value="">Any</option>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Type</label>
                  <select value={randomType} onChange={(e) => setRandomType(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                    <option value="">Any</option>
                    <option value="mcq">Multiple choice</option>
                    <option value="multi">Multiple select</option>
                    <option value="true_false">True / False</option>
                    <option value="short">Short</option>
                    <option value="essay">Essay</option>
                    <option value="fill_blank">Fill blank</option>
                  </select>
                </div>
              </div>
            )}

            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer pt-3 border-t border-slate-200">
              <input type="checkbox" checked={pushAfter} onChange={(e) => setPushAfter(e.target.checked)} />
              <Send className="w-4 h-4" />
              Push to classes immediately after creating
            </label>
          </div>
        )}
      </div>

      <div className="flex justify-between gap-3">
        <button onClick={() => step > 1 ? setStep(step - 1) : nav(base + '/cbt-exams')}
          className="px-4 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm inline-flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" />
          {step === 1 ? 'Back to list' : 'Previous step'}
        </button>
        {step < 5 ? (
          <button onClick={() => stepValid(step) ? setStep(step + 1) : setError('Complete this step first.')}
            disabled={!stepValid(step)}
            className="px-4 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50 inline-flex items-center gap-1">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={submit} disabled={createM.isPending}
            className="px-4 py-2.5 bg-green-600 text-white rounded-lg font-semibold text-sm disabled:opacity-50 inline-flex items-center gap-1">
            <Check className="w-4 h-4" />
            {createM.isPending ? 'Creating…' : 'Create exam'}
          </button>
        )}
      </div>
    </div>
  );
}
